import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DateAdapter, MatNativeDateModule } from '@angular/material/core';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { Store } from '@ngxs/store';
import { AuctionSummaryService, ConfigurationState, DateFormatterService, DateUtilsService, LaunchDarklyService, OrganizationState, SearchService } from 'remarketing-angular-library';
import { take, takeUntil } from 'rxjs';
import { AnalyticsService, SaComponent } from 'smartauction-frontend-micro-library';
import { FormattedDateAdapter } from '../../../../../shared/components/formatted-date-adapter/formatted-date-adapter';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { SpinnerComponent } from 'remarketing-ui-components-lib';

const DateAdapterFactory = function(launchDarklyService) {
  return new FormattedDateAdapter(launchDarklyService.getFeatureFlag('canadianTenantEnabled', false));
};

@Component({
    selector: 'app-sales-report',
    templateUrl: './sales-report.component.html',
    styleUrls: ['./sales-report.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useFactory: DateAdapterFactory,
            deps: [LaunchDarklyService]
        }
    ],
    imports: [
        MatDatepickerModule,
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        SpinnerComponent,
    ]
})
export class SalesReportComponent extends SaComponent implements OnInit, AfterViewInit {
  dealershipName: string;
  tenantShortName: string;
  date: string;
  time: string;
  lastTimestamp: Date;
  tableData: any[];
  unassignedSalesObj: any;
  totalSales: number;
  totalBuyingDealers: number;
  today = new Date();
  fromStartDate = this.today;
  toStartDate = this.today;
  datepicker: UntypedFormGroup;
  toDateFormControl = new UntypedFormControl(this.today);
  fromDateFormControl = new UntypedFormControl(this.today);
  minDateFrom = new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 365);
  maxDateFrom = new Date();
  minDateTo = new Date();
  maxDateTo = new Date();
  dataLoading = true;
  @ViewChild('pickerFrom') pickerFrom: MatDatepicker<any>;
  @ViewChild('pickerTo') pickerTo: MatDatepicker<any>;

  salesReportTranslations = {
    salesReportHeader: '',
    dateRangeSubHeader: '',
    fromDateLabel: '',
    fromDateErrorText: '',
    toDateLabel: '',
    toDateErrorText: '',
    dateRange31Days: '',
    refresh: '',
    sales: '',
    nameTableHeader: '',
    vehiclesSoldTableHeader: '',
    dealerCountTableHeader: '',
    unassignedTableCell: '',
    totalTableCell: '',
    averageTableCell: '',
    reportGenerated: '',
    disclaimerUnassigned: ''
  };

  constructor(
    private readonly store: Store,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly translocoService: TranslocoService,
    private readonly translocoLocaleService: TranslocoLocaleService,
    private readonly dateUtilsService: DateUtilsService,
    private readonly auctionSummaryService: AuctionSummaryService,
    private readonly searchService: SearchService,
    private readonly analyticsService: AnalyticsService,
    private readonly dateFormatterService: DateFormatterService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {
    super();
  }

  get isCanadianTenantEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get fromDate() {
    return this.fromDateFormControl.value;
  }

  get toDate() {
    return this.toDateFormControl.value;
  }

  get fromDateHasError(): boolean {
    return (
      this.datepicker?.controls['fromDateFormControl'].hasError('matDatepickerParse') ||
      this.datepicker.get('fromDateFormControl').invalid
    );
  }

  get toDateHasError(): boolean {
    return (
      this.datepicker?.controls['toDateFormControl'].hasError('matDatepickerParse') ||
      this.datepicker.get('toDateFormControl').invalid
    );
  }

  ngOnInit(): void {
    this.getTranslations();
    this.buildForm();
    this.datepicker.markAllAsTouched();
    this.setDateAndTime();
    this.getTableData();
    this.tenantShortName = this.store.selectSnapshot(ConfigurationState.configuration).tenantShortName;
    this.dealershipName = this.store.selectSnapshot(OrganizationState.organizationState).organization.oeName;
    this.analyticsAtPageload();
  }

  ngAfterViewInit() {
    this.overwriteDatepickerAriaLabels(this.pickerFrom);
    this.overwriteDatepickerAriaLabels(this.pickerTo);
  }

  overwriteDatepickerAriaLabels(picker: MatDatepicker<any>) {
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const iconButton = document.querySelector('.mat-icon-button');

    if (isChrome) {
      picker.openedStream.subscribe(() => {
        this.changeDetectorRef.detectChanges();
        const calendar = document.querySelector('.mat-calendar') as HTMLElement;
        const calendarContent = document.querySelector('.mat-datepicker-content .mat-calendar') as HTMLElement;
        setTimeout(() => {
          iconButton?.setAttribute('aria-label', 'open calendar');
          calendar?.setAttribute('aria-hidden', 'true');
          calendarContent?.setAttribute('aria-hidden', 'true');
        }, 50);
      });
    }
  }

  buildForm() {
    this.fromDateFormControl = new UntypedFormControl(this.today, [Validators.required]);
    this.toDateFormControl = new UntypedFormControl(this.today, [Validators.required]);

    this.datepicker = this.formBuilder.group({
      fromDateFormControl: this.fromDateFormControl,
      toDateFormControl: this.toDateFormControl
    });
  }

  applyErrorStylingClass(error: boolean, errorStyle: string) {
    return error ? `${errorStyle}` : '';
  }

  compareDate(date): number {
    return new Date(date).getTime();
  }

  analyticsAtPageload() {
    this.analyticsService.trackPageLoad({
      page: {
        pageName: `${this.analyticsService.digitalData.page.category.site}:Sell:Sales Report`,
        section: 'Sell:Sales Report',
        site: this.analyticsService.digitalData.page.category.site,
        subsection: 'Sell:Sales Report',
        title: ''
      }
    });
  }

  fromDateIsValid(chosenFromDate) {
    const fromDateIsLessThanToday = chosenFromDate < this.compareDate(this.today);
    const fromDateIsGreaterThanTodayMinusOneYear =
      chosenFromDate > this.compareDate(this.today.getTime() - 1000 * 60 * 60 * 24 * 366);
    return fromDateIsLessThanToday && fromDateIsGreaterThanTodayMinusOneYear;
  }

  toDateIsValid(chosenToDate) {
    const toDateIsLessThanToday = chosenToDate < this.compareDate(this.today);
    const toDateIsGreaterThanFromDate = chosenToDate > this.fromDate;
    const toDateLessThan31DaysAfterFromDate =
      chosenToDate < this.compareDate(this.fromDate?.getTime() + 1000 * 60 * 60 * 24 * 31);
    return toDateIsLessThanToday && toDateIsGreaterThanFromDate && toDateLessThan31DaysAfterFromDate;
  }

  getMaxToDate(fromDate) {
    const isValidFromDatePlus31DaysGreaterThanToday =
      this.compareDate(fromDate.getTime() + 1000 * 60 * 60 * 24 * 31) > this.compareDate(this.today);
    if (isValidFromDatePlus31DaysGreaterThanToday) {
      return this.today;
    } else {
      return new Date(fromDate.getTime() + 1000 * 60 * 60 * 24 * 31);
    }
  }

  updateFromDate(event) {
    this.datepicker.markAllAsTouched();

    if (this.fromDateIsValid(this.compareDate(event.value))) {
      this.fromStartDate = new Date(event.value);
      this.toStartDate = this.maxDateFrom;
      this.minDateTo = new Date(event.value);

      if (this.toDateIsValid(this.toDate)) {
        this.minDateTo = new Date(event.value);
        this.maxDateTo = this.getMaxToDate(new Date(event.value));
      } else {
        this.maxDateTo = this.getMaxToDate(new Date(event.value));
      }
    } else {
      this.maxDateTo = this.today;
      this.minDateTo = this.today;
      this.fromStartDate = this.today;
    }
  }

  updateToDate(event) {
    this.datepicker.markAllAsTouched();

    if (this.toDateIsValid(this.toDate) && this.fromDateIsValid(this.fromDate)) {
      this.minDateTo = this.fromDate;
      this.maxDateTo = this.getMaxToDate(new Date(this.fromDate));
    }
    this.toStartDate = new Date(event.value);
  }

  formatDatesForPayload(date: Date): string {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  setDateAndTime() {
    this.setDate();
    this.setTime();
  }

  setDate() {
    const current = new Date();
    this.date = this.translocoLocaleService.localizeDate(current, 'en-US', { dateStyle: 'medium' });
  }

  setTime(timeStamp?: Date) {
    const frenchLanguage = this.translocoService.getActiveLang().includes('fr');
    const current = new Date();
    // added timestamp logic so when language changes, time updates to correct format, but doesn't change to current date and time
    if (!timeStamp) {
      this.lastTimestamp = current;
    }

    const locale = this.isCanadianTenantEnabled && frenchLanguage ? 'fr-CA' : 'en-US';
    return (this.time = timeStamp
      ? this.translocoLocaleService.localizeDate(timeStamp, locale, { timeStyle: 'short' }).toLowerCase()
      : this.translocoLocaleService.localizeDate(current, locale, { timeStyle: 'short' }).toLowerCase());
  }

  placeholderText() {
    return this.dateFormatterService.placeholderText();
  }

  getTableData() {
    if (!this.fromDateHasError && !this.toDateHasError) {
      this.dataLoading = true;
      if (this.launchDarklyService.getFeatureFlag('sell-salesReportV2Enabled', false)) {
        this.searchService.getSalesTrackerData(this.formatDatesForPayload(this.fromDate), this.formatDatesForPayload(this.toDate))
          .pipe(take(1)).subscribe({
            next: (response) => {
              this.tableData = response.salesReport.filter(item => item.accountManagerName !== 'Unassigned');
              this.unassignedSalesObj = response.salesReport.find(item => item.accountManagerName === 'Unassigned');
              this.totalSales = response.totalSalesCount;
              this.totalBuyingDealers = response.totalBuyingDealershipsCount;
              this.dataLoading = false;
            },
            error: (error) => {
              console.error(error);
              this.dataLoading = false;
            }
          }
          );
      } else {
        this.auctionSummaryService
          .getSalesTrackerData(this.formatDatesForPayload(this.fromDate), this.formatDatesForPayload(this.toDate))
          .pipe(takeUntil(this.destroyed$))
          .subscribe(
            response => {
              this.tableData = response.salesReport.filter(item => item.accountManagerName !== 'Unassigned');
              this.unassignedSalesObj = response.salesReport.find(item => item.accountManagerName === 'Unassigned');
              this.totalSales = response.totalSalesCount;
              this.totalBuyingDealers = response.totalBuyingDealershipsCount;
              this.dataLoading = false;
            },
            error => {
              console.error(error);
            }
          );
      }
    }
  }

  refreshData() {
    this.getTableData();
    this.setDateAndTime();
  }

  getTranslations() {
    const keyParams = {
      'sellModule.salesReport': {}
    };

    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.salesReportTranslations.salesReportHeader = translate[0].salesReportHeader;
        this.salesReportTranslations.dateRangeSubHeader = translate[0].dateRangeSubHeader;
        this.salesReportTranslations.fromDateLabel = translate[0].fromDateLabel;
        this.salesReportTranslations.fromDateErrorText = translate[0].fromDateErrorText;
        this.salesReportTranslations.toDateLabel = translate[0].toDateLabel;
        this.salesReportTranslations.toDateErrorText = translate[0].toDateErrorText;
        this.salesReportTranslations.dateRange31Days = translate[0].dateRange31Days;
        this.salesReportTranslations.refresh = translate[0].refresh;
        this.salesReportTranslations.sales = translate[0].sales;
        this.salesReportTranslations.nameTableHeader = translate[0].nameTableHeader;
        this.salesReportTranslations.vehiclesSoldTableHeader = translate[0].vehiclesSoldTableHeader;
        this.salesReportTranslations.dealerCountTableHeader = translate[0].dealerCountTableHeader;
        this.salesReportTranslations.unassignedTableCell = translate[0].unassignedTableCell;
        this.salesReportTranslations.totalTableCell = translate[0].totalTableCell;
        this.salesReportTranslations.averageTableCell = translate[0].averageTableCell;
        this.salesReportTranslations.reportGenerated = translate[0].reportGenerated;
        this.salesReportTranslations.disclaimerUnassigned = translate[0].disclaimerUnassigned;
        this.setTime(this.lastTimestamp);
      });
  }
}
